/**
 * RenderingStudioLegacyGrid.jsx
 *
 * This is the working R1 implementation lifted wholesale out of
 * RenderingStudio.jsx when the page was restructured into the R2
 * mockup shell. It remains the only live-wired render surface until
 * the R3 backend (selector parser + preflight + line renderer) lands.
 *
 * See the original header comment (preserved below) for lifecycle
 * notes — poll thrash, stale signed URL recovery, job/grid cell
 * shape merging, clearRenderJob on unmount, etc.
 *
 * —— original header ——
 *
 * Phase 4d Step 3 — Rendering Studio R1.
 *
 * The Book Creator's "eye-test" gate: pick a book, pick a character, see the
 * 9-cell speech-sample grid (3 emotions × 3 line variants) annotated with
 * the last successful render (signed audio URL + timestamp), and trigger a
 * sequential server-side TTS run that re-renders every cell. While the run
 * is in flight the page polls the in-process job ledger every ~1.5s and
 * mirrors per-cell status into the cached grid so the table reflects state
 * live without a full re-fetch.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Segment,
  Dropdown,
  Button,
  Loader,
  Message,
  Table,
  Icon,
  Label,
  Header,
} from 'semantic-ui-react';

import {
  fetchRenderCharacters,
  fetchCharacterGrid,
  startGridRender,
  pollGridProgress,
  resignRenderedCell,
  clearRenderJob,
  selectRenderCharacters,
  selectRenderCharactersLoading,
  selectRenderCharactersError,
  selectCharacterGrid,
  selectCharacterGridLoading,
  selectCharacterGridError,
  selectRenderJob,
  selectRenderJobError,
  selectRenderJobInFlight,
} from '../../../slices/voiceRenderingSlice';

import {
  fetchPipeline,
  selectCloneRows,
  selectCloneStatus,
} from '../../../slices/voiceCloningSlice';

const POLL_INTERVAL_MS = 1500;

const cellStatusClass = (status) => {
  switch (status) {
    case 'pending': return 's-pending';
    case 'rendering': return 's-rendering';
    case 'success': return 's-success';
    case 'failed': return 's-failed';
    case 'skipped': return 's-skipped';
    default: return 's-idle';
  }
};

const formatDuration = (ms) => {
  if (!ms || !Number.isFinite(ms)) return '';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
};

const BookPicker = ({ value, onChange }) => {
  const dispatch = useDispatch();
  const cloneRows = useSelector(selectCloneRows);
  const cloneStatus = useSelector(selectCloneStatus);
  const currentUser = useSelector((s) => s.user.profile?.data);

  useEffect(() => {
    if (cloneStatus === 'idle' && currentUser?.userId) {
      dispatch(fetchPipeline(currentUser.userId));
    }
  }, [dispatch, cloneStatus, currentUser?.userId]);

  const bookOptions = useMemo(() => {
    const seen = new Map();
    (cloneRows || []).forEach((row) => {
      if (row?.bookId && !seen.has(row.bookId)) {
        seen.set(row.bookId, row.bookName || row.bookTitle || row.bookId);
      }
    });
    return Array.from(seen.entries()).map(([bookId, name]) => ({
      key: bookId,
      value: bookId,
      text: name,
    }));
  }, [cloneRows]);

  return (
    <Dropdown
      placeholder="Select a book…"
      fluid
      selection
      search
      clearable
      options={bookOptions}
      value={value || ''}
      onChange={(_e, { value: v }) => onChange(v || null)}
      loading={cloneStatus === 'loading'}
      noResultsMessage="No cloned books yet — finish the cloning pipeline first."
    />
  );
};

const CharacterPicker = ({ characters, loading, error, selectedUuid, onSelect }) => {
  if (loading) return <Loader active inline="centered" content="Loading characters…" />;
  if (error) return <Message error compact content={error} />;
  if (!characters || characters.length === 0) {
    return (
      <div className="empty-hint">
        No characters found for this book. (Try the Voice Cloning Pipeline first.)
      </div>
    );
  }
  return (
    <div className="char-picker">
      {characters.map((c) => {
        const ready = Number(c.readyCloneCount || 0);
        const total = Number(c.sampleCount || 0);
        const readyClass = ready > 0 ? (ready === total ? 'is-ready' : '') : 'is-empty';
        return (
          <div
            key={c.uuid}
            className={`char-card${selectedUuid === c.uuid ? ' is-selected' : ''}`}
            onClick={() => onSelect(c.uuid)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') onSelect(c.uuid);
            }}
          >
            <div className="char-name">{c.name || '(unnamed)'}</div>
            {c.role && <div className="char-role">{c.role}</div>}
            <div className="char-stats">
              <span className="char-stat-pill">{total} samples</span>
              <span className={`char-stat-pill ${readyClass}`}>
                {ready} cloned
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const CellAudioPlayer = ({ cell }) => {
  const dispatch = useDispatch();
  const lr = cell?.lastRendered;
  const url = lr?.signedUrl || null;
  const retriesRef = useRef(0);

  const handleError = useCallback(() => {
    if (!lr?.cellId) return;
    if (retriesRef.current >= 2) return;
    retriesRef.current += 1;
    dispatch(resignRenderedCell(lr.cellId));
  }, [dispatch, lr?.cellId]);

  if (!url) return <span style={{ color: '#aaa' }}>(no audio)</span>;
  return (
    <audio
      key={url}
      controls
      preload="none"
      src={url}
      onError={handleError}
      className="grid-audio"
    />
  );
};

const GridTable = ({ grid, job }) => {
  if (!grid) return null;
  const cells = Array.isArray(grid.cells) ? grid.cells : [];
  if (cells.length === 0) {
    return <div className="empty-hint">This character has no CharSpeechSamples yet.</div>;
  }
  const activeCellId = job?.currentCellId || null;
  return (
    <Table compact celled className="rs-grid-table">
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell>Sample</Table.HeaderCell>
          <Table.HeaderCell>Emotion</Table.HeaderCell>
          <Table.HeaderCell>Text</Table.HeaderCell>
          <Table.HeaderCell>Status</Table.HeaderCell>
          <Table.HeaderCell>Audio</Table.HeaderCell>
          <Table.HeaderCell>Meta</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {cells.map((c) => {
          const status = c.status || (c.lastRendered ? 'idle' : 'idle');
          const isActive =
            activeCellId &&
            (activeCellId === c.characterSampleId ||
              activeCellId === c.lastRendered?.cellId);
          const reason = c.reason || c.error || null;
          return (
            <Table.Row
              key={c.characterSampleId}
              className={`grid-row${isActive ? ' is-active' : ''}`}
            >
              <Table.Cell><strong>{c.sampleName || c.characterSampleId}</strong></Table.Cell>
              <Table.Cell>
                {c.emotionCategory || '—'}
                {c.intensity ? (
                  <Label size="mini" style={{ marginLeft: 4 }}>{c.intensity}</Label>
                ) : null}
              </Table.Cell>
              <Table.Cell>
                <div className="grid-text">{c.sampleText || '(no text)'}</div>
              </Table.Cell>
              <Table.Cell>
                <span className={`grid-cell-status ${cellStatusClass(status)}`}>{status}</span>
                {reason && (
                  <div style={{ fontSize: '0.75rem', color: '#8a5a00', marginTop: 4 }}>
                    {reason}
                  </div>
                )}
              </Table.Cell>
              <Table.Cell><CellAudioPlayer cell={c} /></Table.Cell>
              <Table.Cell>
                <div style={{ fontSize: '0.75rem', color: '#666' }}>
                  {c.lastRendered?.durationMs && <div>dur {formatDuration(c.lastRendered.durationMs)}</div>}
                  {c.lastRendered?.updatedAt && (
                    <div>{new Date(c.lastRendered.updatedAt).toLocaleString()}</div>
                  )}
                  {!c.cloneIsActive && <div style={{ color: '#b8362e' }}>no active clone</div>}
                </div>
              </Table.Cell>
            </Table.Row>
          );
        })}
      </Table.Body>
    </Table>
  );
};

const ProgressBar = ({ job, error }) => {
  if (!job) return null;
  const { totalCells = 0, summary = {}, status, finishedAt } = job;
  const done = (summary.success || 0) + (summary.failed || 0) + (summary.skipped || 0);
  const pct = totalCells > 0 ? Math.round((done / totalCells) * 100) : 0;
  const stateClass = status === 'complete' ? 's-complete' : status === 'failed' ? 's-failed' : '';
  return (
    <div className={`progress-shell ${stateClass}`}>
      <div>
        <strong>Render job:</strong>{' '}
        <span className={`grid-cell-status ${cellStatusClass(status === 'running' ? 'rendering' : status)}`}>
          {status}
        </span>
      </div>
      <div className="progress-track">
        <div className="progress-fill" style={{ width: `${pct}%` }} />
      </div>
      <div className="progress-meta">
        <span>{done}/{totalCells} cells</span>
        <span>✔ {summary.success || 0}</span>
        <span>⏭ {summary.skipped || 0}</span>
        <span>✖ {summary.failed || 0}</span>
        {finishedAt && <span>finished {new Date(finishedAt).toLocaleTimeString()}</span>}
        {job.elevenLabsMock && <span style={{ color: '#8a6d00' }}>(mock TTS)</span>}
      </div>
      {error && (
        <Message warning size="tiny" style={{ marginTop: 8 }} content={`Polling: ${error}`} />
      )}
    </div>
  );
};

const RenderingStudioLegacyGrid = () => {
  const dispatch = useDispatch();
  const [bookId, setBookId] = useState(null);
  const [characterUuid, setCharacterUuid] = useState(null);

  const charsPayload = useSelector((s) => selectRenderCharacters(s, bookId));
  const charsLoading = useSelector((s) => selectRenderCharactersLoading(s, bookId));
  const charsError = useSelector((s) => selectRenderCharactersError(s, bookId));

  const grid = useSelector((s) => selectCharacterGrid(s, characterUuid));
  const gridLoading = useSelector((s) => selectCharacterGridLoading(s, characterUuid));
  const gridError = useSelector((s) => selectCharacterGridError(s, characterUuid));

  const job = useSelector(selectRenderJob);
  const jobError = useSelector(selectRenderJobError);
  const jobInFlight = useSelector(selectRenderJobInFlight);

  const handleBookChange = useCallback((next) => {
    setBookId(next);
    setCharacterUuid(null);
    dispatch(clearRenderJob());
  }, [dispatch]);

  const handleCharacterChange = useCallback((next) => {
    setCharacterUuid(next);
    dispatch(clearRenderJob());
  }, [dispatch]);

  useEffect(() => {
    if (bookId) dispatch(fetchRenderCharacters(bookId));
  }, [dispatch, bookId]);

  useEffect(() => {
    if (bookId && characterUuid) {
      dispatch(fetchCharacterGrid({ bookId, characterUuid }));
    }
  }, [dispatch, bookId, characterUuid]);

  useEffect(() => {
    if (!job?.jobId) return undefined;
    if (!jobInFlight) return undefined;
    const id = setInterval(() => {
      dispatch(pollGridProgress(job.jobId));
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [dispatch, job?.jobId, jobInFlight]);

  useEffect(() => () => { dispatch(clearRenderJob()); }, [dispatch]);

  const handleRenderAll = useCallback(() => {
    if (!bookId || !characterUuid) return;
    dispatch(startGridRender({ bookId, characterUuid }));
  }, [dispatch, bookId, characterUuid]);

  const characters = charsPayload?.characters || [];
  const selectedCharacter = useMemo(
    () => characters.find((c) => c.uuid === characterUuid) || null,
    [characters, characterUuid]
  );

  const renderDisabled =
    !bookId ||
    !characterUuid ||
    jobInFlight ||
    (selectedCharacter && Number(selectedCharacter.readyCloneCount || 0) === 0);

  return (
    <div className="rendering-studio">
      <Segment>
        <Header as="h4">1. Pick a book</Header>
        <BookPicker value={bookId} onChange={handleBookChange} />
      </Segment>

      {bookId && (
        <Segment>
          <Header as="h4">2. Pick a character</Header>
          <CharacterPicker
            characters={characters}
            loading={charsLoading}
            error={charsError}
            selectedUuid={characterUuid}
            onSelect={handleCharacterChange}
          />
        </Segment>
      )}

      {bookId && characterUuid && (
        <Segment>
          <Header as="h4" style={{ display: 'flex', alignItems: 'center' }}>
            <span>3. Eye-test grid</span>
            <span style={{ flex: 1 }} />
            <Button
              primary
              disabled={renderDisabled}
              loading={jobInFlight}
              onClick={handleRenderAll}
            >
              <Icon name="play" />
              {jobInFlight ? 'Rendering…' : 'Render all cells'}
            </Button>
          </Header>

          {selectedCharacter &&
            Number(selectedCharacter.readyCloneCount || 0) === 0 && (
              <Message
                warning
                size="tiny"
                content={
                  'No cloned voices available for this character — every cell ' +
                  'would be skipped. Finish the Voice Cloning Pipeline first.'
                }
              />
            )}

          <ProgressBar job={job} error={jobError} />

          {gridLoading && <Loader active inline="centered" content="Loading grid…" />}
          {gridError && <Message error compact content={gridError} />}
          {!gridLoading && !gridError && <GridTable grid={grid} job={job} />}
        </Segment>
      )}
    </div>
  );
};

export default RenderingStudioLegacyGrid;
